import {useState, useEffect, useRef} from 'react';
import {
  Row,
  Col,
  Popover,
  Form,
  Tooltip,
} from 'antd';

import {getLocalesByKey as t} from '@/utils/translation';

import {MixPanel} from '@/utils/mixpanel';
import {useStore} from '@/store/root-store';
import {
  CommonInput,
  Button,
} from '@/components/common-components/components';
import debounce from 'lodash/debounce';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCaretDown} from '@fortawesome/pro-solid-svg-icons';
import {faArrowLeft, faGlobe, faInfoCircle, faPlus, faTimes} from '@fortawesome/pro-light-svg-icons';
import {observer} from 'mobx-react-lite';
import classNames from 'classnames';
import {faCheck, faExclamationTriangle, faSearch} from '@fortawesome/pro-regular-svg-icons';
import {SCA_PAGE_ADD_KEYWORD} from '@/constants/events';
import {SingleKeywordDataCard} from './SingleKeywordDataCard';
import styles from '../style.module.scss';
import {formatNumber, numberWithCommas} from '@/utils/number';


interface Props {
  parentRef: any;
  expanded: boolean;
  getCustomerQuota?: () => {};
  showSidebarPaymentDrawer?: any;
}

const Input = CommonInput;

const useClickedOnPageHandler = (ref, callback) => {
  useEffect(() => {
    const handleClickOutside = event => {
      event.stopPropagation();
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [ref]);
};


export const TargetKeywords = observer(({expanded, getCustomerQuota, showSidebarPaymentDrawer}: Props) => {
  const {
    countries: {getSpecificCountryByCode},
    contentOptimizer: {
      currentPage,
    }} = useStore('');
  const {settings: {customer: {profile: {quotaUtilization}}}} = useStore('');
  const {contentOptimizer: {currentPage: {isPublic}}} = useStore('');


  const [form] = Form.useForm();
  const [showKwErrorMsg, setShowKwErrorMsg] = useState({
    showMessage: false,
    message: '',
  });
  const [isAddingNewKw, setIsAddingNewKw] = useState(false);
  const [newKwValue, setNewKwValue] = useState('');
  const [kwLengthErr, setKwLengthErr] = useState({
    showMessage: false,
    message: '',
  });
  const targetrKeywordsContainer = useRef(null);

  useEffect(() => {
    const addHorizontalScroll = e => {
      // If we are scrolling inside suggestions container
      if (targetrKeywordsContainer.current && targetrKeywordsContainer.current.contains(e.target)) {
        // Disable vertical scroll for main container
        const topScroll = window.scrollY;
        const leftScroll = window.scrollX;
        window.onscroll = e => {
          if (!expanded) {
            e.preventDefault();
            window.scrollTo(leftScroll, topScroll);
          }
        };
        // Scroll suggestions container
        if (e.deltaY > 0) {
          if (e.deltaY > 50) {
            targetrKeywordsContainer.current.scrollLeft += 30;
          } else {
            targetrKeywordsContainer.current.scrollLeft += e.deltaY;
          }
        } else {
          if ((e.deltaY * -1) > 50) {
            targetrKeywordsContainer.current.scrollLeft -= 30;
          } else {
            targetrKeywordsContainer.current.scrollLeft -= (e.deltaY * -1);
          }
        }
      } else if (targetrKeywordsContainer.current && !targetrKeywordsContainer.current.contains(e.target)) {
        // Enable vertical scroll for main container
        window.onscroll = () => {};
      }
    };

    document.addEventListener('wheel', addHorizontalScroll);

    return () => {
      document.removeEventListener('wheel', addHorizontalScroll);
    };
  }, [targetrKeywordsContainer, expanded]);

  const [showLocationSelector, setShowLocationSelector] = useState(false);
  const [newKwLocation, setNewKwLocation] = useState({
    countryCode: currentPage.content.countryCode || '',
    location: currentPage.content.location || '',
    locationId: currentPage.content.locationId || null,
  });
  const [isEditDefaultLocation, setIsEditDefaultLocation] = useState(false);

  const popoverRef = useRef(null);


  useClickedOnPageHandler(popoverRef, async () => {
    await new Promise(r => setTimeout(r, 100));
    setShowLocationSelector(false);
  });


  const onAddKeywordFinish = async (values: {[key: string]: string}) => {
    if (kwLengthErr.showMessage) return;
    if ('location-search' in values) return;

    const formKeyword = Object.keys(values)
      .map(key => values[key])
      .filter(item => item !== undefined && item !== '' && item && item.trim().length);
    const format = /[`~!@#$%^*()_+\-=[\]{};':"\\|,.<>/?]+/;


    if (!formKeyword[0]) {
      setShowKwErrorMsg({
        showMessage: true,
        message: 'Please type a Target Keyword.',
      });
      return;
    }

    if (format.test(formKeyword[0])) {
      setShowKwErrorMsg({
        showMessage: true,
        message: 'Only characters and numbers are allowed',
      });
      return;
    }
    if (formKeyword[0]?.length > 160 || formKeyword[0]?.split(' ')?.length > 20) {
      setShowKwErrorMsg({
        showMessage: true,
        message: 'Max 20 words or 160 char. allowed',
      });
      return;
    }
    // if (currentPage.feTargetKeywords?.filter(kw => kw.keyword.toLowerCase() === formKeyword[0].trim().toLowerCase() && kw.locationId == newKwLocation.locationId).length) {
    //   setShowKwErrorMsg({
    //     showMessage: true,
    //     message: 'This keyword already exists for selected location.',
    //   });
    //   return;
    // }
    // we are making sure to save target kw params the same way we are sending them to BE
    // we are still keeping null values as we will get those from the BE
    let keywordForFrontendArray;
    if (newKwLocation.locationId && newKwLocation.location) {
      keywordForFrontendArray = {
        keyword: formKeyword[0].trim().toLowerCase(),
        countryCode: '',
        locationId: newKwLocation.locationId,
        location: newKwLocation.location,
      };
    } else if (newKwLocation.countryCode) {
      keywordForFrontendArray = {
        keyword: formKeyword[0].trim().toLowerCase(),
        countryCode: newKwLocation.countryCode,
        locationId: null,
        location: null,
      };
    } else if (!newKwLocation.locationId && !newKwLocation.countryCode) {
      keywordForFrontendArray = {
        keyword: formKeyword[0].trim().toLowerCase(),
        countryCode: '',
        locationId: null,
        location: null,
      };
    }

    if (currentPage.feTargetKeywords?.filter(kw => JSON.stringify({
      keyword: kw.keyword,
      countryCode: kw.countryCode,
      locationId: kw.locationId,
      location: kw.location,
    }) === JSON.stringify({
      keyword: keywordForFrontendArray.keyword,
      countryCode: keywordForFrontendArray.countryCode,
      locationId: keywordForFrontendArray.locationId,
      location: keywordForFrontendArray.location,
    })).length) {
      setShowKwErrorMsg({
        showMessage: true,
        message: 'This keyword already exists for selected location.',
      });
      return;
    }


    const formatArray = [...currentPage.feTargetKeywords, {...keywordForFrontendArray}];
    currentPage.updateFeTargetKeywords(formatArray);

    form.resetFields();

    setShowKwErrorMsg({
      showMessage: false,
      message: '',
    });

    try {
      await currentPage?.editKeywords({keyword: formKeyword[0].trim().toLowerCase(), ...newKwLocation}, 'add');
      getCustomerQuota();
    } catch (err) {
      if (err?.response?.status === 429) {
        showSidebarPaymentDrawer(null, {title: 'You need more Content Genius focus terms quota to continue', subtitle: '', text: `Current limit: ${quotaUtilization?.ca?.allowedFocusTerms?.total} lookups`});
      }

      return Promise.reject(err);
    }

    setNewKwLocation({
      countryCode: currentPage.content.countryCode,
      location: currentPage.content.location,
      locationId: currentPage.content.locationId,
    });

    setNewKwValue('');
  };


  const locationDebouncerChange = debounce( async (country: string) => {
    try {
      await currentPage?.updateCountriesList(country);
    } catch (err) {
      return Promise.reject(err);
    }
  }, 500);


  const searchForLocation = (location?: string) => {
    if (location && location !== '') {
      locationDebouncerChange(location);
    }
  };

  /* When selecting location */
  const locationSelected = async (location: string, locationId: number, reach: number, countryCode: string) => {
    // if we are in default location selection mode and if selected location doesnt match default location
    if (isEditDefaultLocation ) {
      if (!locationId && !countryCode) {
        currentPage.updatelocationData('', '', null, null);
      }

      if (locationId !== currentPage.content.locationId || (!locationId && !currentPage.content.locationId && location)) {
        currentPage.updatelocationData(countryCode, location, locationId, reach);
      }

      setNewKwLocation({
        countryCode: currentPage.content.countryCode || '',
        location: currentPage.content.location || '',
        locationId: currentPage.content.locationId || null,
      });
    }

    // If we are not in default location selection mode, set location for that target keyword
    if (!isEditDefaultLocation) {
      if (locationId && (locationId !== newKwLocation.locationId) || countryCode && (countryCode !== newKwLocation.countryCode) || (location?.length === 0 && !locationId && !reach && countryCode?.length === 0 )) {
        setNewKwLocation({
          location: location?.length === 0 && !locationId && !reach && countryCode?.length === 0 ? 'Worldwide': location,
          locationId: locationId,
          countryCode: countryCode,
        });

        setShowLocationSelector(false);
        setIsEditDefaultLocation(false);
      }
    }
  };

  const updateKwHandler = e => {
    setNewKwValue(e.target.value);
    if (e.target.value.length > 160) {
      setKwLengthErr({
        message: 'Keyword can have only 160 characters.',
        showMessage: true,
      });
    } else {
      if (kwLengthErr.showMessage) {
        setKwLengthErr({
          message: '',
          showMessage: false,
        });
      }
    }
  };

  return (
    <div className={styles.targetKwContainer} ref={targetrKeywordsContainer}>
      {!isPublic && currentPage.feTargetKeywords?.length >= 15 ? <Tooltip title={`You've reached the maximum number of 15 target keywords per article.`}>
        <div className={classNames(styles.newKwFormContainer, styles.disabledBtn)}>
          <div className={classNames(styles.addNewKwBtn, styles.forbiddenBtn)}>
            <div className={styles.plusIconStyle}>
              <FontAwesomeIcon icon={faPlus} className={styles.plusIcon}/>
            </div>
          </div>
        </div>
      </Tooltip> :
        !isPublic && <div className={classNames(styles.newKwFormContainer, (isAddingNewKw || !currentPage.feTargetKeywords?.length) ? styles.newFormStyle : '', !currentPage.feTargetKeywords?.length ? styles.newEmphasizedFormStyle : '')}>
          {(isAddingNewKw || !currentPage.feTargetKeywords?.length) ?
            <>
              <Form className={styles.addNewKwForm} form={form} onFinish={onAddKeywordFinish}>
                <div className={styles.newKwInputContainer} >
                  <Tooltip visible={kwLengthErr.showMessage} title={kwLengthErr.message}>
                    <Form.Item name='keyword1' shouldUpdate >
                      <Input inputType='text' variant='transparent' placeholder={!currentPage.feTargetKeywords?.length ? 'Add a target keyword' : t('new-keyword')} className={styles.customInputField} value={newKwValue} onChange={updateKwHandler}/>
                    </Form.Item>
                  </Tooltip>
                  {/* {showKwErrorMsg.showMessage && <Text style={{display: 'block', fontSize: '12px', color: '#ff6262', maxWidth: '260px', whiteSpace: 'nowrap', textAlign: 'left'}}>{showKwErrorMsg.message}</Text>} */}
                  <div style={{display: 'flex'}}>
                    <Popover
                      overlayClassName={styles.countrySelectorPopover}
                      trigger='click'
                      placement='bottomRight'
                      mouseLeaveDelay={0}
                      mouseEnterDelay={0.5}
                      openClassName={styles.openPopover}
                      visible={showLocationSelector}
                      content={
                        <div style={{color: '#fff'}} ref={popoverRef}>
                          {isEditDefaultLocation && <>
                            <FontAwesomeIcon style={{color: 'black', cursor: 'pointer'}} icon={faArrowLeft} onClick={e => {
                              e.stopPropagation();
                              setIsEditDefaultLocation(false);
                            }}/>
                            <span style={{color: '#000', marginLeft: '10px'}}>SELECT DEFAULT LOCATION</span></>}

                          <Form
                            name='locations'
                          >
                            <Form.Item
                              name='location-search'
                            >
                              <div style={{display: 'flex'}}>
                                <span style={{margin: 'auto'}}><FontAwesomeIcon style={{color: '#A3A4A4', marginRight: '5px'}} icon={faSearch}/></span>
                                <CommonInput
                                  name='1'
                                  onChange={e => searchForLocation(e.target.value)}
                                  className={styles.customInput}
                                  inputType='text'
                                  variant='transparent'
                                  placeholder={'Search for location'} />
                                {!isEditDefaultLocation && <Tooltip placement='bottom' title={'You can target the same keyword for multiple locations.'} trigger='click'>
                                  <span style={{margin: 'auto'}}><FontAwesomeIcon style={{color: '#A3A4A4', marginRight: '5px'}} icon={faInfoCircle}/></span>
                                </Tooltip>}
                              </div>
                            </Form.Item>
                          </Form>
                          <div className={styles.divider}></div>
                          <div>
                            <Row className={styles.columnTitlesRow}>
                              <Col className={styles.columnTitle}>LOCATION</Col>
                              <Col className={styles.columnTitle}>REACH</Col>
                            </Row>
                            <>
                              <Row style={{justifyContent: 'space-between'}}>
                                <Col style={{color: '#000', maxWidth: '350px'}}>
                                  {currentPage.content.location || getSpecificCountryByCode(currentPage.content.countryCode)?.name || currentPage.content.countryCode ||
                                (<span><FontAwesomeIcon icon={faGlobe}/> Worldwide</span>)}
                                </Col>
                                <Col style={{color: '#000'}}>{formatNumber(currentPage.content.reach)}</Col>
                              </Row>
                              <span className={styles.defaultLocationName} style={{color: 'gray'}}>{`Default location. `}</span>{!isEditDefaultLocation && <span style={{color: '#88ace0', cursor: 'pointer'}} onClick={e => {
                                e.stopPropagation();
                                setIsEditDefaultLocation(true);
                              }}>Change?</span>}
                            </>
                            <div className={styles.divider}></div>
                            <Row
                              className={classNames(
                                styles.singleCountryRow,
                                isEditDefaultLocation && !currentPage.content.locationId && !currentPage.content.countryCode || !isEditDefaultLocation && !newKwLocation.locationId && !newKwLocation.countryCode ? styles.singleCountryRowSelected : null)}
                              onClick={() => {
                                // if this location in list matches either default or current kw location, disable onClickEvent
                                if (isEditDefaultLocation && (currentPage.content.locationId || currentPage.content.countryCode) || !isEditDefaultLocation) {
                                  locationSelected('', null, null, '');
                                }
                              }}>
                              <Col className={styles.locationInfo}>
                                <span><FontAwesomeIcon icon={faGlobe}/> Worldwide</span>
                                {isEditDefaultLocation && !currentPage.content.locationId && !currentPage.content.location || !isEditDefaultLocation && !newKwLocation.locationId && !newKwLocation.countryCode ? <span><FontAwesomeIcon style={{color: '#7f4ead', marginLeft: '5px'}} icon={faCheck}/></span> : null}
                                {/* {isEditDefaultLocation && !currentPage.content.locationId && !currentPage.content.location ? <p className={styles.defaultLocationName}>Default location.</p> : null} */}
                              </Col>
                              <Col style={{color: '#000'}} className={classNames(styles.locationInfo, styles.setAsDefaultText, isEditDefaultLocation && currentPage.content.locationId && styles.isEditDefaultLocation)}>{'Set as default'}</Col>
                            </Row>
                            {currentPage?.countries && currentPage?.countries.length ? currentPage.countries.map((country, id) => {
                              return <Row key={id}
                                className={classNames(
                                  styles.singleCountryRow,
                                  isEditDefaultLocation && currentPage.content.locationId === country.locationId || !isEditDefaultLocation && newKwLocation.locationId === country.locationId ? styles.singleCountryRowSelected : null)}
                                onClick={() => {
                                  // if this location in list matches either default or current kw location, disable onClickEvent
                                  if (isEditDefaultLocation && (currentPage.content.locationId !== country.locationId) || !isEditDefaultLocation) {
                                    // locationSelected(country.location, country.locationId, country.reach, country.countryCode);
                                    locationSelected(country.location, country.locationId === 2840 ? null : country.locationId, country.reach, country.locationId === 2840 ? 'US' : country.countryCode);
                                  }
                                }}>
                                <Col className={styles.locationInfo}>
                                  {country.location}
                                  {isEditDefaultLocation && (currentPage.content.locationId === country.locationId) || !isEditDefaultLocation && newKwLocation.locationId === country.locationId ? <span><FontAwesomeIcon style={{color: '#7f4ead', marginLeft: '5px'}} icon={faCheck}/></span> : null}
                                  {/* {isEditDefaultLocation && currentPage.content.locationId === country.locationId ? <p className={styles.defaultLocationName}>Default location.</p> : null} */}
                                </Col>
                                <Col style={{color: '#000'}} className={classNames(styles.locationInfo, styles.countryReach, isEditDefaultLocation && currentPage.content.locationId !== country.locationId && styles.isEditDefaultLocation)}>{formatNumber(country.reach)}</Col>
                                <Col style={{color: '#000'}} className={classNames(styles.locationInfo, styles.setAsDefaultText, isEditDefaultLocation && currentPage.content.locationId !== country.locationId && styles.isEditDefaultLocation)}>{'Set as default'}</Col>
                              </Row>;
                            }) : <Row
                              className={classNames(
                                styles.singleCountryRow,
                                isEditDefaultLocation && currentPage.content.locationId == 2840 || !isEditDefaultLocation && newKwLocation.locationId == 2840 ? styles.singleCountryRowSelected : null)}
                              onClick={() => {
                                // if this location in list matches either default or current kw location, disable onClickEvent
                                if (isEditDefaultLocation && (currentPage.content.locationId != 2840) || !isEditDefaultLocation) {
                                  locationSelected('United States', 2840, null, 'US');
                                }
                              }}>
                              <Col className={styles.locationInfo}>
                                <span>United States</span>
                                {isEditDefaultLocation && currentPage.content.locationId == 2840 || !isEditDefaultLocation && newKwLocation.locationId == 2840 ? <span><FontAwesomeIcon style={{color: '#7f4ead', marginLeft: '5px'}} icon={faCheck}/></span> : null}
                              </Col>
                              <Col style={{color: '#000'}} className={classNames(styles.locationInfo, styles.setAsDefaultText, isEditDefaultLocation && currentPage.content.locationId != 2840 && styles.isEditDefaultLocation)}>{'Set as default'}</Col>
                            </Row>}
                          </div>

                        </div>
                      }
                    >
                      <div
                        style={{textAlign: 'left', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 100}}
                        onClick={e => {
                          e.stopPropagation();
                          setShowLocationSelector(!showLocationSelector);
                        }}>
                        {newKwLocation.location || currentPage.content.location || getSpecificCountryByCode(currentPage.content.countryCode)?.name || currentPage.content.countryCode || (<span><FontAwesomeIcon icon={faGlobe}/> Worldwide</span>)}
                        <FontAwesomeIcon icon={faCaretDown} style={{marginLeft: '5px'}}/>
                      </div>
                      {/* <FontAwesomeIcon
                    icon={faMapMarkerAlt}
                    className={styles.addNewKeywordLocationIcon}
                    onClick={e => {
                      e.stopPropagation();
                      setShowLocationSelector(!showLocationSelector);
                    }}/> */}
                    </Popover>
                    <div className={styles.newKwInputControllsContainer}>
                      {showKwErrorMsg.showMessage && <Tooltip title={showKwErrorMsg.message}><FontAwesomeIcon icon={faExclamationTriangle} color='red' style={{marginTop: 'auto'}}/></Tooltip>}
                      <Form.Item shouldUpdate>
                        <Button
                          buttonType='transparent'
                          type='submit'
                          className={classNames(styles.populateButton, styles.confirmButton)}
                          disabled={kwLengthErr.showMessage}
                        >
                          <FontAwesomeIcon icon={faCheck} />
                        </Button>
                      </Form.Item>
                      <Form.Item>
                        <Button
                          buttonType='transparent'
                          className={classNames(styles.populateButton, styles.cancelButton)}
                          onClick={() => {
                            setIsAddingNewKw(false);
                            setShowKwErrorMsg({
                              showMessage: false,
                              message: '',
                            });
                          }}
                          // disabled={formIsComplete.length <= 0}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </Button>
                      </Form.Item>
                    </div>
                  </div>

                </div>


              </Form>

            </> :
            <>
              <div className={styles.addNewKwBtn} onClick={() => setIsAddingNewKw(true)}>
                <div className={styles.plusIconStyle}>
                  <FontAwesomeIcon icon={faPlus} className={styles.plusIcon}/>
                </div>
                <div className={styles.addKeywordStyle}>
                  <div className={styles.addNewKwBtnLabel}>Add keyword</div>
                  <div style={{display: 'flex'}}>
                    <div className={styles.quota}>{t('quota')}</div>
                    <div className={styles.quotaValues}>{numberWithCommas(quotaUtilization?.ca?.allowedFocusTerms?.consumed)} / {numberWithCommas(quotaUtilization?.ca?.allowedFocusTerms?.total)}</div>
                  </div>
                </div>
              </div>
            </>}
        </div>}
      {currentPage?.feTargetKeywords &&
          // if we want to reverse we want to update index count below as well
          // currentPage?.feTargetKeywords?.reverse()?.map((kw, index)
          currentPage?.feTargetKeywords?.map((kw, index) => (
            <SingleKeywordDataCard
              key={index}
              // bellow idx count is done if the data array is reversed,
              // and we want to match indexes of the original array for removing/updating keywords
              // idx={store?.feTargetKeywords?.length - index - 1}
              idx={index}
              kw={kw}
              store={currentPage}
              keywordsFormProps={currentPage.feTargetKeywords}
              editKeywords={currentPage?.editKeywords}
              callback={keywords => {
                MixPanel.track(SCA_PAGE_ADD_KEYWORD, {
                  'pageId': currentPage?.content.uuid,
                  'keywords': keywords,
                });
              }}/>
          ))}

      {/* {!currentPage?.feTargetKeywords?.length && currentPage.isFetchingTargetKw?
        <div className={styles.targetKwLoadingMsg} style={{marginLeft: expanded ? '0' : '-40px'}}>Loading target keywords...</div> : null} */}
    </div>
  );
});
